import { useEffect, useState } from 'react';
import { IconButton, useMediaQuery, useTheme } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { PickersShortcutsItem } from '@mui/x-date-pickers/PickersShortcuts';
import { DateRange } from '@mui/x-date-pickers-pro/models';
import { dayjs, Dayjs, displayDateFormats, grey } from '@mgk-eld/utils';
import { CalendarIcon } from '@mgk-eld/assets';
import { popperSx } from '../Calendar';

type Props = {
  handleChange: (start: string, end: string) => void;
  timezone?: string;
  indicateTZ?: boolean;
  defaults?: DateRange<Dayjs>;
  withShortCuts?: boolean;
  withShortCutsB?: boolean;
  withShortCutsQuarter?: boolean;
  withMonths?: boolean;
  placeholder?: string;
};

const presetRangeSetA: PickersShortcutsItem<DateRange<Dayjs>>[] = [
  {
    label: 'Last 8 Days',
    getValue: () => {
      const today = dayjs().tz();
      return [today.subtract(8, 'day'), today];
    },
  },
  {
    label: 'Last 30 Days',
    getValue: () => {
      const today = dayjs().tz();
      return [today.subtract(30, 'day'), today];
    },
  },
];

const generateQuarterShortcuts = (): PickersShortcutsItem<
  DateRange<Dayjs>
>[] => {
  const year = dayjs().year();
  return [1, 2, 3, 4].map((q) => {
    const start = dayjs(`${year}-01-01`).quarter(q).startOf('quarter');
    const end = dayjs(`${year}-01-01`).quarter(q).endOf('quarter');
    return {
      label: `Q${q} ${year}`,
      getValue: () => [start, end],
    };
  });
};

const presetQuarterSet: PickersShortcutsItem<DateRange<Dayjs>>[] = [
  {
    label: 'Previous Quarter',
    getValue: () => {
      const today = dayjs().tz().subtract(1, 'quarter');
      return [today.startOf('quarter'), today.endOf('quarter')];
    },
  },
  ...generateQuarterShortcuts(),
];

const presetRangeSetB: PickersShortcutsItem<DateRange<Dayjs>>[] = [
  {
    label: 'Last 8 Days',
    getValue: () => {
      const today = dayjs().tz();
      return [today.subtract(8, 'day'), today];
    },
  },
  {
    label: 'Last 14 Days',
    getValue: () => {
      const today = dayjs().tz();
      return [today.subtract(14, 'day'), today];
    },
  },
];

const presetMonthlySet: PickersShortcutsItem<DateRange<Dayjs>>[] = [
  {
    label: 'Current month',
    getValue: () => {
      const today = dayjs().tz();
      const startOfMonth = today.startOf('month');
      const endOfMonth = today.endOf('month');
      const endDate = today.isBefore(endOfMonth) ? today : endOfMonth;
      return [startOfMonth, endDate];
    },
  },
  {
    label: 'Last month',
    getValue: () => {
      const today = dayjs().tz();
      const startOfLastMonth = today.subtract(1, 'month').startOf('month');
      const endOfLastMonth = today.subtract(1, 'month').endOf('month');
      return [startOfLastMonth, endOfLastMonth];
    },
  },
];

export const DateRangePickerPro: React.FC<Props> = ({
  timezone,
  defaults,
  withShortCuts,
  withShortCutsB,
  withShortCutsQuarter,
  withMonths,
  indicateTZ,
  handleChange,
  placeholder,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [value, setValue] = useState<DateRange<Dayjs>>(() => {
    if (!defaults || !defaults[0] || !defaults[1]) return [null, null];
    return [defaults[0].tz(timezone, true), defaults[1].tz(timezone, true)];
  });

  useEffect(() => {
    const [start, end] = value;
    const today = dayjs().tz(timezone);

    if (start && end) {
      const adjustedStart = start.isAfter(today) ? today : start;
      const adjustedEnd = end.isAfter(today) ? today : end;

      if (!adjustedStart.isSame(start) || !adjustedEnd.isSame(end)) {
        setValue([
          adjustedStart.tz(timezone, true),
          adjustedEnd.tz(timezone, true),
        ]);
      }

      handleChange(
        adjustedStart.format('YYYY-MM-DD'),
        adjustedEnd.format('YYYY-MM-DD')
      );
    } else if (!start && !end) {
      handleChange('', '');
    }
  }, [value, handleChange, timezone]);

  const customFormat = () => {
    const [start, end] = value;

    if (start && end) {
      const startInTz = timezone ? start.tz(timezone) : start;
      const endInTz = timezone ? end.tz(timezone) : end;

      if (indicateTZ && timezone) {
        return `${startInTz.format(
          displayDateFormats.mmmDDyyyy
        )} - ${endInTz.format(displayDateFormats.mmmDDyyyy + ' z')}`;
      }

      return `${startInTz.format(
        displayDateFormats.mmmDDyyyy
      )} - ${endInTz.format(displayDateFormats.mmmDDyyyy)}`;
    }

    return '';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DemoContainer components={['SingleInputDateRangeField']} sx={{ pt: 0 }}>
        <DateRangePicker
          name="allowedRange"
          calendars={isMobile ? 1 : 2}
          slots={{
            field: SingleInputDateRangeField,
          }}
          format="MM/DD/YYYY"
          timezone={timezone}
          value={value}
          disableFuture
          onChange={(newValue) => setValue(newValue)}
          slotProps={{
            shortcuts: {
              items: withMonths
                ? presetMonthlySet
                : withShortCuts
                ? presetRangeSetA
                : withShortCutsB
                ? presetRangeSetB
                : withShortCutsQuarter
                ? presetQuarterSet
                : [],
            },
            layout: {
              sx: {
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row-reverse',
              },
            },
            field: {
              clearable: true,
              dateSeparator: '-',
            },
            textField: {
              size: 'small',
              placeholder: placeholder ?? 'Report dates range',
              InputProps: {
                value: customFormat(),
                endAdornment: (
                  <IconButton size="small">
                    <CalendarIcon />
                  </IconButton>
                ),
              },
              sx: {
                '& fieldset': { border: 'none' },
                '& div': { height: '40px' },
                '& input': {
                  color: 'black',
                  WebkitTextFillColor: 'unset !important',
                },
                border: `1px solid ${grey[100]}`,
                borderRadius: '8px',
                background: grey[50],
                height: '40px',
                minWidth: isMobile ? '100%' : '270px !important',
              },
            },
            popper: {
              sx: popperSx,
            },
          }}
        />
      </DemoContainer>
    </LocalizationProvider>
  );
};
